import React, { useEffect, useState, useRef } from 'react';
import { XIcon, SendIcon, Loader2Icon, LeafIcon, TrendingUpIcon, BarChart3Icon, FileTextIcon, MenuIcon, PlusIcon, CalendarIcon, CheckCircleIcon, ArrowRightIcon } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
interface AIMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
  widgetIds?: string[];
  isDemo?: boolean;
}
interface GeneratedWidget {
  id: string;
  type: 'chart' | 'stat' | 'table' | 'action-list' | 'report';
  title: string;
  data: any;
  timestamp: Date;
}
interface ChatHistory {
  id: string;
  title: string;
  messages: AIMessage[];
  widgets: GeneratedWidget[];
  timestamp: Date;
}
interface GreenbackAIChatProps {
  isOpen: boolean;
  onClose: () => void;
}
const GreenbackAIChat: React.FC<GreenbackAIChatProps> = ({
  isOpen,
  onClose
}) => {
  const [messages, setMessages] = useState<AIMessage[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [hasStartedChat, setHasStartedChat] = useState(false);
  const [generatedWidgets, setGeneratedWidgets] = useState<GeneratedWidget[]>([]);
  const [chatHistory, setChatHistory] = useState<ChatHistory[]>([]);
  const [currentChatId, setCurrentChatId] = useState<string | null>(null);
  const [isChatHistoryOpen, setIsChatHistoryOpen] = useState(false);
  const [addedActions, setAddedActions] = useState<Set<number>>(new Set());
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const examplePrompts = [{
    icon: TrendingUpIcon,
    text: 'Calcola il mio impatto CO2 annuale'
  }, {
    icon: LeafIcon,
    text: 'Suggeriscimi azioni concrete per ridurre le emissioni'
  }, {
    icon: BarChart3Icon,
    text: 'Mostrami i miei progressi negli ultimi mesi'
  }, {
    icon: FileTextIcon,
    text: 'Genera un report del mio impatto sostenibile'
  }];
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({
      behavior: 'smooth'
    });
  };
  useEffect(() => {
    scrollToBottom();
  }, [messages]);
  useEffect(() => {
    if (isOpen && !hasStartedChat && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen, hasStartedChat]);
  const handleAddAction = (actionId: number) => {
    setAddedActions(prev => new Set(prev).add(actionId));
  };
  const generateWidget = (type: string, userMessage: string): GeneratedWidget | null => {
    const lowerMessage = userMessage.toLowerCase();
    if (lowerMessage.includes('co2') || lowerMessage.includes('impatto') || lowerMessage.includes('calcola')) {
      return {
        id: Date.now().toString(),
        type: 'stat',
        title: 'Il Tuo Impatto CO2',
        data: {
          stats: [{
            label: 'CO2 Annuale',
            value: '8.2t',
            change: '+0.5t vs media italiana',
            icon: '🌍'
          }, {
            label: 'Potenziale Riduzione',
            value: '3.5t',
            change: 'Con azioni suggerite',
            icon: '📉'
          }, {
            label: 'Risparmio Possibile',
            value: '€680',
            change: "All'anno",
            icon: '💰'
          }, {
            label: 'Obiettivo Parigi',
            value: '2.5t',
            change: 'Target 2030',
            icon: '🎯'
          }]
        },
        timestamp: new Date()
      };
    }
    if (lowerMessage.includes('progress') || lowerMessage.includes('andamento') || lowerMessage.includes('ultimi mesi')) {
      return {
        id: Date.now().toString(),
        type: 'chart',
        title: 'Riduzione CO2 negli Ultimi 6 Mesi',
        data: {
          months: ['Gen', 'Feb', 'Mar', 'Apr', 'Mag', 'Giu'],
          co2Saved: [0.3, 0.5, 0.8, 1.2, 1.8, 2.4],
          target: [0.5, 0.5, 0.5, 0.5, 0.5, 0.5]
        },
        timestamp: new Date()
      };
    }
    if (lowerMessage.includes('azioni') || lowerMessage.includes('suggeris')) {
      return {
        id: Date.now().toString(),
        type: 'action-list',
        title: 'Azioni Concrete per Te',
        data: {
          actions: [{
            id: 1,
            title: 'Usa bici per tragitti brevi',
            category: 'Mobilità',
            impact: '1.2t CO2/anno',
            savings: '€280',
            difficulty: 'Media',
            icon: '🚲'
          }, {
            id: 2,
            title: 'Termostato a 19-20°C + LED',
            category: 'Energia',
            impact: '0.9t CO2/anno',
            savings: '€180',
            difficulty: 'Bassa',
            icon: '💡'
          }, {
            id: 3,
            title: 'Riduci carne rossa 2x/settimana',
            category: 'Alimentazione',
            impact: '0.5t CO2/anno',
            savings: '€120',
            difficulty: 'Media',
            icon: '🥗'
          }, {
            id: 4,
            title: 'Elettrodomestici classe A+++',
            category: 'Energia',
            impact: '0.4t CO2/anno',
            savings: '€95',
            difficulty: 'Alta',
            icon: '⚡'
          }, {
            id: 5,
            title: 'Compostaggio domestico',
            category: 'Rifiuti',
            impact: '0.2t CO2/anno',
            savings: '€45',
            difficulty: 'Bassa',
            icon: '♻️'
          }]
        },
        timestamp: new Date()
      };
    }
    if (lowerMessage.includes('report')) {
      return {
        id: Date.now().toString(),
        type: 'report',
        title: 'Report Impatto Sostenibile Completo',
        data: {
          overview: {
            totalCO2: '8.2t',
            reduction: '2.4t',
            savings: '€340',
            ranking: 'Top 15%'
          },
          categoryBreakdown: [{
            category: 'Mobilità',
            co2: '3.2t',
            percentage: 39,
            color: '#ef4444'
          }, {
            category: 'Energia',
            co2: '2.8t',
            percentage: 34,
            color: '#f59e0b'
          }, {
            category: 'Alimentazione',
            co2: '1.5t',
            percentage: 18,
            color: '#10b981'
          }, {
            category: 'Rifiuti',
            co2: '0.7t',
            percentage: 9,
            color: '#3b82f6'
          }],
          monthlyTrend: {
            months: ['Gen', 'Feb', 'Mar', 'Apr', 'Mag', 'Giu'],
            values: [8.5, 8.3, 8.0, 7.8, 7.5, 8.2]
          },
          detailedTable: {
            headers: ['Categoria', 'Attuale', 'Obiettivo 2030', 'Gap', 'Progresso'],
            rows: [['Mobilità', '3.2t', '1.0t', '-2.2t', '25%'], ['Energia', '2.8t', '0.8t', '-2.0t', '30%'], ['Alimentazione', '1.5t', '0.5t', '-1.0t', '40%'], ['Rifiuti', '0.7t', '0.2t', '-0.5t', '35%']]
          }
        },
        timestamp: new Date()
      };
    }
    return null;
  };
  const generateAIResponse = (userMessage: string): {
    content: string;
    widget: GeneratedWidget | null;
  } => {
    const lowerMessage = userMessage.toLowerCase();
    let content = '';
    let widget: GeneratedWidget | null = null;
    if (lowerMessage.includes('co2') || lowerMessage.includes('impatto') || lowerMessage.includes('calcola')) {
      content = `
        <h3>🌍 Il Tuo Impatto CO2 Annuale</h3>
        <p style="margin: 12px 0;">Basandomi sul tuo profilo e sui corsi completati, stimo che la tua impronta di carbonio annuale sia di circa <strong>8.2 tonnellate di CO2</strong>.</p>
        
        <h4>📊 Confronto</h4>
        <ul>
          <li>Media italiana: 7.5t CO2/anno</li>
          <li>Obiettivo Parigi 2030: 2.5t CO2/anno</li>
          <li>Il tuo potenziale di riduzione: <strong>-3.5t CO2/anno</strong></li>
        </ul>
        
        <h4>💡 Come Ridurlo</h4>
        <ul>
          <li>🚲 <strong>Mobilità</strong>: Bici per tragitti brevi → -1.2t CO2/anno</li>
          <li>💡 <strong>Energia</strong>: Ottimizza riscaldamento → -0.8t CO2/anno</li>
          <li>🥗 <strong>Alimentazione</strong>: Riduci carne rossa → -0.5t CO2/anno</li>
        </ul>
      `;
      widget = generateWidget('stat', userMessage);
    } else if (lowerMessage.includes('progress') || lowerMessage.includes('andamento') || lowerMessage.includes('ultimi mesi')) {
      content = `
        <h3>📈 I Tuoi Progressi</h3>
        <p style="margin: 12px 0;">Negli ultimi 6 mesi hai fatto progressi significativi:</p>
        
        <ul>
          <li>✅ <strong>CO2 Risparmiata</strong>: 2.4 tonnellate (+28% vs periodo precedente)</li>
          <li>✅ <strong>Azioni Implementate</strong>: 23 azioni concrete</li>
          <li>✅ <strong>Corsi Completati</strong>: 2 su 3 attivi</li>
          <li>✅ <strong>Risparmio Economico</strong>: €340 risparmiati</li>
        </ul>
        
        <p style="margin: 12px 0;">🎯 Sei nel <strong>top 15%</strong> degli studenti per impatto totale!</p>
      `;
      widget = generateWidget('chart', userMessage);
    } else if (lowerMessage.includes('azioni') || lowerMessage.includes('suggeris') || lowerMessage.includes('concrete')) {
      content = `
        <h3>✅ Azioni Concrete per Ridurre le Emissioni</h3>
        <p style="margin: 12px 0;">Ho selezionato le 5 azioni più efficaci per il tuo profilo. Ogni azione può essere aggiunta al tuo tracker personale per monitorare i progressi.</p>
        
        <p style="margin: 12px 0;">Le azioni sono ordinate per <strong>impatto totale</strong> (riduzione CO2 + risparmio economico). Clicca su "Aggiungi alle mie azioni" per iniziare a tracciarle!</p>
      `;
      widget = generateWidget('action-list', userMessage);
    } else if (lowerMessage.includes('report')) {
      content = `
        <h3>📄 Report Impatto Sostenibile Completo</h3>
        <p style="margin: 12px 0;">Ho generato il tuo report personalizzato con analisi dettagliata del tuo impatto ambientale.</p>
        
        <p style="margin: 12px 0;">Il report include:</p>
        <ul>
          <li>📊 <strong>Breakdown per categoria</strong>: Mobilità, Energia, Alimentazione, Rifiuti</li>
          <li>📈 <strong>Trend temporale</strong>: Evoluzione degli ultimi 6 mesi</li>
          <li>🎯 <strong>Confronto obiettivi</strong>: Gap vs target Parigi 2030</li>
          <li>⭐ <strong>Ranking</strong>: La tua posizione nella community</li>
        </ul>
      `;
      widget = generateWidget('report', userMessage);
    } else if (lowerMessage.includes('corso') || lowerMessage.includes('suggeris')) {
      content = `
        <h3>📚 Corsi Consigliati per Te</h3>
        <p style="margin: 12px 0;">In base al tuo percorso di apprendimento, ti consiglio:</p>
        
        <h4>🏠 Casa Efficiente: energia, bollette e comfort</h4>
        <ul>
          <li>Durata: 3 settimane</li>
          <li>Impatto: -2.1t CO2/anno</li>
          <li>Match: <strong>95%</strong> con il tuo profilo</li>
        </ul>
        
        <h4>🚲 Mobilità sostenibile: muoversi meglio, spendere meno</h4>
        <ul>
          <li>Durata: 2 settimane</li>
          <li>Impatto: -1.5t CO2/anno</li>
          <li>Match: <strong>88%</strong> con il tuo profilo</li>
        </ul>
        
        <p style="margin: 12px 0;">Entrambi i corsi sono molto pratici e ti aiuteranno a risparmiare anche economicamente!</p>
      `;
    } else {
      content = `
        <h3>👋 Come posso aiutarti?</h3>
        <p style="margin: 12px 0;">Posso aiutarti con:</p>
        
        <ul>
          <li>🎓 <strong>Apprendimento</strong>: Suggerimenti su corsi e percorsi formativi</li>
          <li>📊 <strong>Impatto</strong>: Calcolo della tua impronta di carbonio</li>
          <li>💡 <strong>Azioni</strong>: Consigli pratici per ridurre le emissioni</li>
          <li>🌍 <strong>Clima</strong>: Spiegazioni su cambiamento climatico e sostenibilità</li>
        </ul>
        
        <p style="margin: 12px 0;">Cosa ti interessa approfondire?</p>
      `;
    }
    return {
      content,
      widget
    };
  };
  const handleSendMessage = async (content?: string) => {
    const messageContent = content || inputValue.trim();
    if (!messageContent) return;
    if (!hasStartedChat) {
      setHasStartedChat(true);
    }
    const userMessage: AIMessage = {
      id: Date.now().toString(),
      role: 'user',
      content: messageContent,
      timestamp: new Date()
    };
    setMessages(prev => [...prev, userMessage]);
    setInputValue('');
    setIsTyping(true);
    const minDelay = 1000 + Math.random() * 1000;
    const startTime = Date.now();
    const {
      content: aiContent,
      widget
    } = generateAIResponse(messageContent);
    const elapsedTime = Date.now() - startTime;
    const remainingDelay = Math.max(0, minDelay - elapsedTime);
    if (remainingDelay > 0) {
      await new Promise(resolve => setTimeout(resolve, remainingDelay));
    }
    let widgetIds: string[] = [];
    if (widget) {
      setGeneratedWidgets(prev => [...prev, widget]);
      widgetIds = [widget.id];
    }
    const aiMessage: AIMessage = {
      id: (Date.now() + 1).toString(),
      role: 'assistant',
      content: aiContent,
      timestamp: new Date(),
      widgetIds,
      isDemo: true
    };
    setMessages(prev => [...prev, aiMessage]);
    setIsTyping(false);
    saveChatHistory([...messages, userMessage, aiMessage], widget ? [...generatedWidgets, widget] : generatedWidgets);
  };
  const saveChatHistory = (msgs: AIMessage[], widgets: GeneratedWidget[]) => {
    if (currentChatId) {
      setChatHistory(prev => prev.map(chat => chat.id === currentChatId ? {
        ...chat,
        messages: msgs,
        widgets,
        timestamp: new Date()
      } : chat));
    } else {
      const newChatId = Date.now().toString();
      const firstUserMessage = msgs.find(m => m.role === 'user');
      const newChat: ChatHistory = {
        id: newChatId,
        title: firstUserMessage?.content.slice(0, 50) || 'Nuova conversazione',
        messages: msgs,
        widgets,
        timestamp: new Date()
      };
      setChatHistory(prev => [newChat, ...prev]);
      setCurrentChatId(newChatId);
    }
  };
  const loadChat = (chat: ChatHistory) => {
    setCurrentChatId(chat.id);
    setMessages(chat.messages);
    setGeneratedWidgets(chat.widgets);
    setHasStartedChat(true);
    setIsChatHistoryOpen(false);
  };
  const startNewChat = () => {
    setCurrentChatId(null);
    setMessages([]);
    setGeneratedWidgets([]);
    setHasStartedChat(false);
    setIsChatHistoryOpen(false);
    setAddedActions(new Set());
  };
  const renderWidget = (widget: GeneratedWidget) => {
    if (widget.type === 'stat') {
      return <div className="grid grid-cols-2 gap-3" data-id="element-44">
          {widget.data.stats.map((stat: any, idx: number) => <div key={idx} className="p-4 bg-gray-50 rounded-lg border border-gray-200" data-id="element-45">
              <div className="text-2xl mb-1" data-id="element-46">{stat.icon}</div>
              <p className="text-2xl font-semibold text-gray-900 mb-1" data-id="element-47">
                {stat.value}
              </p>
              <p className="text-xs text-gray-600 font-normal mb-1" data-id="element-48">
                {stat.label}
              </p>
              <span className="text-xs text-green-600 bg-green-50 px-2 py-0.5 rounded-full font-medium" data-id="element-49">
                {stat.change}
              </span>
            </div>)}
        </div>;
    }
    if (widget.type === 'chart') {
      return <div className="bg-gray-50 rounded-lg p-4 border border-gray-200" data-id="element-50">
          <div className="space-y-3" data-id="element-51">
            {widget.data.months.map((month: string, idx: number) => <div key={idx} className="flex items-center gap-3" data-id="element-52">
                <span className="text-xs text-gray-600 w-8 font-normal" data-id="element-53">
                  {month}
                </span>
                <div className="flex-1 bg-gray-200 rounded-full h-6 relative overflow-hidden" data-id="element-54">
                  <motion.div initial={{
                width: 0
              }} animate={{
                width: `${widget.data.co2Saved[idx] / 3 * 100}%`
              }} transition={{
                duration: 0.8,
                delay: idx * 0.1
              }} className="bg-green-600 h-full rounded-full" data-id="element-55"></motion.div>
                  <span className="absolute inset-0 flex items-center justify-center text-xs font-medium text-gray-900" data-id="element-56">
                    {widget.data.co2Saved[idx]}t CO2
                  </span>
                </div>
              </div>)}
          </div>
        </div>;
    }
    if (widget.type === 'action-list') {
      return <div className="space-y-3" data-id="element-57">
          {widget.data.actions.map((action: any, idx: number) => {
          const isAdded = addedActions.has(action.id);
          return <motion.div key={action.id} initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.3,
            delay: idx * 0.1
          }} className="p-4 bg-gray-50 rounded-lg border border-gray-200 hover:border-gray-300 transition-colors" data-id="element-58">
                <div className="flex items-start gap-3" data-id="element-59">
                  <div className="text-3xl" data-id="element-60">{action.icon}</div>
                  <div className="flex-1 min-w-0" data-id="element-61">
                    <h4 className="text-sm font-semibold text-gray-900 mb-1" data-id="element-62">
                      {action.title}
                    </h4>
                    <div className="flex flex-wrap items-center gap-2 mb-3" data-id="element-63">
                      <span className="text-xs px-2 py-0.5 rounded-full bg-blue-50 text-blue-700 font-medium" data-id="element-64">
                        {action.category}
                      </span>
                      <span className="text-xs text-gray-600" data-id="element-65">
                        Difficoltà:{' '}
                        <span className="font-medium" data-id="element-66">{action.difficulty}</span>
                      </span>
                    </div>
                    <div className="flex items-center gap-4 mb-3" data-id="element-67">
                      <div className="flex items-center gap-1" data-id="element-68">
                        <span className="text-xs text-gray-600" data-id="element-69">Impatto:</span>
                        <span className="text-xs font-semibold text-green-600" data-id="element-70">
                          {action.impact}
                        </span>
                      </div>
                      <div className="flex items-center gap-1" data-id="element-71">
                        <span className="text-xs text-gray-600" data-id="element-72">
                          Risparmio:
                        </span>
                        <span className="text-xs font-semibold text-green-600" data-id="element-73">
                          {action.savings}
                        </span>
                      </div>
                    </div>
                    {!isAdded ? <button onClick={() => handleAddAction(action.id)} className="flex items-center gap-1.5 px-3 py-1.5 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors text-xs font-medium" data-id="element-74">
                        <PlusIcon size={14} strokeWidth={2} data-id="element-75" />
                        Aggiungi alle mie azioni
                      </button> : <div className="flex items-center gap-1.5 px-3 py-1.5 bg-green-50 border border-green-200 text-green-700 rounded-lg text-xs font-medium" data-id="element-76">
                        <CheckCircleIcon size={14} strokeWidth={2} data-id="element-77" />
                        Aggiunta con successo
                      </div>}
                  </div>
                </div>
              </motion.div>;
        })}
        </div>;
    }
    if (widget.type === 'report') {
      return <div className="space-y-4" data-id="element-78">
          {/* Overview Stats */}
          <div className="grid grid-cols-2 gap-3" data-id="element-79">
            <div className="p-3 bg-gray-50 rounded-lg border border-gray-200" data-id="element-80">
              <p className="text-xs text-gray-600 mb-1" data-id="element-81">CO2 Totale</p>
              <p className="text-xl font-semibold text-gray-900" data-id="element-82">
                {widget.data.overview.totalCO2}
              </p>
            </div>
            <div className="p-3 bg-green-50 rounded-lg border border-green-200" data-id="element-83">
              <p className="text-xs text-green-700 mb-1" data-id="element-84">Riduzione</p>
              <p className="text-xl font-semibold text-green-700" data-id="element-85">
                {widget.data.overview.reduction}
              </p>
            </div>
            <div className="p-3 bg-blue-50 rounded-lg border border-blue-200" data-id="element-86">
              <p className="text-xs text-blue-700 mb-1" data-id="element-87">Risparmio</p>
              <p className="text-xl font-semibold text-blue-700" data-id="element-88">
                {widget.data.overview.savings}
              </p>
            </div>
            <div className="p-3 bg-yellow-50 rounded-lg border border-yellow-200" data-id="element-89">
              <p className="text-xs text-yellow-700 mb-1" data-id="element-90">Ranking</p>
              <p className="text-xl font-semibold text-yellow-700" data-id="element-91">
                {widget.data.overview.ranking}
              </p>
            </div>
          </div>

          {/* Category Breakdown */}
          <div className="bg-gray-50 rounded-lg p-4 border border-gray-200" data-id="element-92">
            <h4 className="text-sm font-semibold text-gray-900 mb-3" data-id="element-93">
              Breakdown per Categoria
            </h4>
            <div className="space-y-3" data-id="element-94">
              {widget.data.categoryBreakdown.map((cat: any, idx: number) => <div key={idx} data-id="element-95">
                  <div className="flex justify-between items-center mb-1" data-id="element-96">
                    <span className="text-xs font-medium text-gray-700" data-id="element-97">
                      {cat.category}
                    </span>
                    <span className="text-xs font-semibold text-gray-900" data-id="element-98">
                      {cat.co2}
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2" data-id="element-99">
                    <motion.div initial={{
                  width: 0
                }} animate={{
                  width: `${cat.percentage}%`
                }} transition={{
                  duration: 0.8,
                  delay: idx * 0.1
                }} className="h-full rounded-full" style={{
                  backgroundColor: cat.color
                }} data-id="element-100"></motion.div>
                  </div>
                </div>)}
            </div>
          </div>

          {/* Monthly Trend */}
          <div className="bg-gray-50 rounded-lg p-4 border border-gray-200" data-id="element-101">
            <h4 className="text-sm font-semibold text-gray-900 mb-3" data-id="element-102">
              Trend Ultimi 6 Mesi
            </h4>
            <div className="space-y-2" data-id="element-103">
              {widget.data.monthlyTrend.months.map((month: string, idx: number) => <div key={idx} className="flex items-center gap-3" data-id="element-104">
                    <span className="text-xs text-gray-600 w-8" data-id="element-105">{month}</span>
                    <div className="flex-1 bg-gray-200 rounded-full h-5 relative overflow-hidden" data-id="element-106">
                      <motion.div initial={{
                  width: 0
                }} animate={{
                  width: `${widget.data.monthlyTrend.values[idx] / 10 * 100}%`
                }} transition={{
                  duration: 0.8,
                  delay: idx * 0.1
                }} className="bg-blue-600 h-full rounded-full" data-id="element-107"></motion.div>
                      <span className="absolute inset-0 flex items-center justify-center text-xs font-medium text-gray-900" data-id="element-108">
                        {widget.data.monthlyTrend.values[idx]}t
                      </span>
                    </div>
                  </div>)}
            </div>
          </div>

          {/* Detailed Table */}
          <div className="overflow-x-auto bg-white rounded-lg border border-gray-200" data-id="element-109">
            <table className="w-full text-xs" data-id="element-110">
              <thead className="bg-gray-50" data-id="element-111">
                <tr data-id="element-112">
                  {widget.data.detailedTable.headers.map((header: string, idx: number) => <th key={idx} className="text-left py-2 px-3 text-xs font-semibold text-gray-700 border-b border-gray-200" data-id="element-113">
                        {header}
                      </th>)}
                </tr>
              </thead>
              <tbody data-id="element-114">
                {widget.data.detailedTable.rows.map((row: string[], idx: number) => <tr key={idx} className="border-b border-gray-100 last:border-0" data-id="element-115">
                      {row.map((cell, cellIdx) => <td key={cellIdx} className="py-2 px-3 text-xs text-gray-900 font-normal" data-id="element-116">
                          {cell}
                        </td>)}
                    </tr>)}
              </tbody>
            </table>
          </div>

          {/* Download Button */}
          <button className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition-colors text-sm font-medium" data-id="element-117">
            <FileTextIcon size={16} strokeWidth={1.5} data-id="element-118" />
            Scarica Report PDF
          </button>
        </div>;
    }
    return null;
  };
  if (!isOpen) return null;
  return <motion.div initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} exit={{
    opacity: 0
  }} className="fixed inset-0 z-50 bg-gray-50 flex flex-col" data-id="element-119">
      {/* Header with gradient outline logo */}
      <div className="px-4 sm:px-6 py-4 border-b border-gray-200 bg-white flex-shrink-0" data-id="element-120">
        <div className="flex items-center justify-between" data-id="element-121">
          <div className="flex items-center gap-3" data-id="element-122">
            <button onClick={() => setIsChatHistoryOpen(!isChatHistoryOpen)} className="p-2 hover:bg-gray-100 rounded-lg transition-colors" data-id="element-123">
              <MenuIcon size={20} strokeWidth={1.5} className="text-gray-600" data-id="element-124" />
            </button>
            {/* Logo with gradient outline and black logo */}
            <div className="w-10 h-10 rounded-full flex items-center justify-center p-[2px]" style={{
            background: 'linear-gradient(135deg, rgba(255, 0, 77, 1) 0%, rgba(116, 10, 214, 1) 100%)'
          }} data-id="element-125">
              <div className="w-full h-full rounded-full flex items-center justify-center bg-white p-2" data-id="element-126">
                <img src="https://cdn.magicpatterns.com/uploads/w4Sqqi6gRHWGegGAQT2PTG/Greenback_logo_(1).png" alt="Greenback" className="w-5 h-5 object-contain" data-id="element-127" />
              </div>
            </div>
            <div data-id="element-128">
              <h1 className="text-lg font-semibold text-gray-900" data-id="element-129">
                GB Intelligence
              </h1>
              <p className="text-xs text-gray-500 font-normal" data-id="element-130">
                Analisi e suggerimenti sostenibili
              </p>
            </div>
          </div>
          <button onClick={onClose} className="flex items-center gap-2 px-4 py-2 hover:bg-gray-100 rounded-lg transition-colors text-sm font-medium text-gray-700" data-id="element-131">
            <XIcon size={16} strokeWidth={1.5} data-id="element-132" />
            <span className="hidden sm:inline" data-id="element-133">Esci</span>
          </button>
        </div>
      </div>

      {/* Chat History Sidebar */}
      <AnimatePresence data-id="element-134">
        {isChatHistoryOpen && <>
            <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} className="fixed inset-0 bg-gray-900 bg-opacity-50 z-40" onClick={() => setIsChatHistoryOpen(false)} data-id="element-135" />
            <motion.div initial={{
          x: -300
        }} animate={{
          x: 0
        }} exit={{
          x: -300
        }} transition={{
          type: 'spring',
          damping: 25,
          stiffness: 200
        }} className="fixed left-0 top-0 bottom-0 w-80 bg-white shadow-xl z-50 flex flex-col" data-id="element-136">
              <div className="p-4 border-b border-gray-200" data-id="element-137">
                <h2 className="text-base font-semibold text-gray-900 mb-3" data-id="element-138">
                  Storico Chat
                </h2>
                <div className="p-[2px]" style={{
              background: 'linear-gradient(90deg, rgba(255, 0, 77, 1) 0%, rgba(116, 10, 214, 1) 100%)',
              borderRadius: '100px'
            }} data-id="element-139">
                  <button onClick={startNewChat} className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-white hover:bg-gray-50 transition-colors text-sm font-medium text-gray-900" style={{
                borderRadius: '100px'
              }} data-id="element-140">
                    <PlusIcon size={16} strokeWidth={2} data-id="element-141" />
                    Nuova Chat
                  </button>
                </div>
              </div>
              <div className="flex-1 overflow-y-auto p-4 space-y-2" data-id="element-142">
                {chatHistory.map(chat => <button key={chat.id} onClick={() => loadChat(chat)} className={`w-full text-left p-3 rounded-lg transition-colors ${currentChatId === chat.id ? 'bg-gray-50 border border-gray-300' : 'hover:bg-gray-50 border border-gray-200'}`} data-id="element-143">
                    <p className="text-sm font-medium text-gray-900 mb-1 line-clamp-1" data-id="element-144">
                      {chat.title}
                    </p>
                    <div className="flex items-center gap-2 text-xs text-gray-500 font-normal" data-id="element-145">
                      <CalendarIcon size={12} strokeWidth={1.5} data-id="element-146" />
                      <span data-id="element-147">
                        {chat.timestamp.toLocaleDateString('it-IT', {
                    day: 'numeric',
                    month: 'short'
                  })}
                      </span>
                      <span data-id="element-148">•</span>
                      <span data-id="element-149">{chat.messages.length} messaggi</span>
                    </div>
                  </button>)}
                {chatHistory.length === 0 && <div className="text-center py-8 text-sm text-gray-500 font-normal" data-id="element-150">
                    Nessuna chat salvata
                  </div>}
              </div>
            </motion.div>
          </>}
      </AnimatePresence>

      {/* Main Content */}
      {!hasStartedChat ?
    // Welcome Screen
    <div className="flex-1 flex items-center justify-center p-4 sm:p-6 overflow-y-auto" data-id="element-151">
          <div className="w-full max-w-3xl" data-id="element-152">
            <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.2
        }} className="text-center mb-8 sm:mb-12" data-id="element-153">
              {/* Logo with gradient outline and black logo */}
              <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 p-[2px]" style={{
            background: 'linear-gradient(135deg, rgba(255, 0, 77, 1) 0%, rgba(116, 10, 214, 1) 100%)'
          }} data-id="element-154">
                <div className="w-full h-full rounded-full flex items-center justify-center bg-white p-3" data-id="element-155">
                  <img src="https://cdn.magicpatterns.com/uploads/w4Sqqi6gRHWGegGAQT2PTG/Greenback_logo_(1).png" alt="Greenback" className="w-8 h-8 object-contain" data-id="element-156" />
                </div>
              </div>
              <h2 className="text-2xl font-semibold text-gray-900 mb-2" data-id="element-157">
                Come posso aiutarti oggi?
              </h2>
              <p className="text-sm text-gray-600 font-normal" data-id="element-158">
                Chiedi analisi, visualizzazioni o suggerimenti sul tuo impatto
                sostenibile
              </p>
            </motion.div>

            <motion.form initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.4
        }} onSubmit={e => {
          e.preventDefault();
          handleSendMessage();
        }} className="relative mb-8" data-id="element-159">
              <input ref={inputRef} type="text" value={inputValue} onChange={e => setInputValue(e.target.value)} placeholder="Fai una richiesta a GB Intelligence..." className="w-full px-6 py-4 pr-14 border border-gray-300 rounded-full focus:outline-none focus:ring-2 focus:border-transparent text-sm font-normal shadow-sm" data-id="element-160" />
              <button type="submit" disabled={!inputValue.trim()} className="absolute right-2 top-1/2 -translate-y-1/2 p-2.5 bg-green-600 hover:bg-green-700 text-white rounded-full transition-colors disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-161">
                <SendIcon size={18} strokeWidth={1.5} data-id="element-162" />
              </button>
            </motion.form>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-id="element-163">
              {examplePrompts.map((example, idx) => <motion.button key={idx} initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.5 + idx * 0.15
          }} onClick={() => {
            setInputValue(example.text);
            handleSendMessage(example.text);
          }} className="flex items-start gap-3 p-4 bg-white border border-gray-200 rounded-xl hover:border-gray-300 hover:shadow-sm transition-all text-left" data-id="element-164">
                  <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 bg-green-50" data-id="element-165">
                    <example.icon size={20} className="text-green-600" strokeWidth={1.5} data-id="element-166" />
                  </div>
                  <p className="text-sm text-gray-900 font-normal" data-id="element-167">
                    {example.text}
                  </p>
                </motion.button>)}
            </div>
          </div>
        </div> :
    // Active Chat
    <div className="flex-1 flex flex-col overflow-hidden" data-id="element-168">
          <div className="flex-1 overflow-y-auto p-4 sm:p-6" data-id="element-169">
            <div className="max-w-4xl mx-auto space-y-6" data-id="element-170">
              {messages.map(message => <div key={message.id} className={`flex gap-3 ${message.role === 'user' ? 'justify-end' : 'justify-start'}`} data-id="element-171">
                  {message.role === 'assistant' && <div className="w-8 h-8 rounded-full flex items-center justify-center p-[2px]" style={{
              background: 'linear-gradient(135deg, rgba(255, 0, 77, 1) 0%, rgba(116, 10, 214, 1) 100%)'
            }} data-id="element-172">
                      <div className="w-full h-full rounded-full flex items-center justify-center bg-white p-1.5" data-id="element-173">
                        <img src="https://cdn.magicpatterns.com/uploads/w4Sqqi6gRHWGegGAQT2PTG/Greenback_logo_(1).png" alt="Greenback" className="w-4 h-4 object-contain" data-id="element-174" />
                      </div>
                    </div>}

                  <div className={`max-w-[85%] space-y-2 ${message.role === 'user' ? 'order-2' : 'order-1'}`} data-id="element-175">
                    {message.isDemo && <span className="inline-block px-3 py-1 text-xs font-medium" style={{
                background: 'linear-gradient(90deg, rgba(255, 0, 77, 0.1) 0%, rgba(116, 10, 214, 0.1) 100%)',
                borderRadius: '100px',
                border: '1px solid rgba(116, 10, 214, 0.2)',
                color: '#740AD6'
              }} data-id="element-176">
                        Dati Simulati
                      </span>}

                    {/* Message bubble with outline styles */}
                    {message.role === 'user' ? <div className="p-[2px]" style={{
                background: '#16a34a',
                borderRadius: '16px'
              }} data-id="element-177">
                        <div className="rounded-2xl px-4 py-3 bg-white" data-id="element-178">
                          <p className="text-sm font-normal leading-relaxed text-gray-900" data-id="element-179">
                            {message.content}
                          </p>
                        </div>
                      </div> : <div className="p-[2px]" style={{
                background: 'linear-gradient(135deg, rgba(255, 0, 77, 1) 0%, rgba(116, 10, 214, 1) 100%)',
                borderRadius: '16px'
              }} data-id="element-180">
                        <div className="rounded-2xl px-4 py-3 bg-white" data-id="element-181">
                          <div className="prose prose-sm max-w-none
                            prose-headings:font-semibold prose-headings:text-gray-900
                            prose-h3:text-base prose-h3:mb-2 prose-h3:mt-0
                            prose-h4:text-sm prose-h4:mb-2 prose-h4:mt-3
                            prose-p:text-gray-700 prose-p:leading-relaxed prose-p:font-normal
                            prose-ul:text-gray-700 prose-ul:font-normal prose-ul:leading-relaxed prose-ul:my-2
                            prose-ol:text-gray-700 prose-ol:font-normal prose-ol:leading-relaxed prose-ol:my-2
                            prose-li:my-1
                            prose-strong:font-semibold prose-strong:text-gray-900" dangerouslySetInnerHTML={{
                    __html: message.content
                  }} data-id="element-182" />
                        </div>
                      </div>}

                    {message.widgetIds?.map(widgetId => {
                const widget = generatedWidgets.find(w => w.id === widgetId);
                if (!widget) return null;
                return <div key={widgetId} className="bg-white border border-gray-200 rounded-xl p-4" data-id="element-183">
                          <h3 className="text-sm font-semibold text-gray-900 mb-3" data-id="element-184">
                            {widget.title}
                          </h3>
                          {renderWidget(widget)}
                        </div>;
              })}

                    <div className="text-xs text-gray-400 font-normal" data-id="element-185">
                      {message.timestamp.toLocaleTimeString('it-IT', {
                  hour: '2-digit',
                  minute: '2-digit'
                })}
                    </div>
                  </div>

                  {message.role === 'user' && <div className="w-8 h-8 rounded-full border-2 border-green-600 overflow-hidden flex-shrink-0" data-id="element-186">
                      <img src="https://cdn.magicpatterns.com/uploads/wAuXNp8Njv5DkmF4gEUeC7/jonathan-cooper-ViyA5myhBVw-unsplash.jpg" alt="User" className="w-full h-full object-cover" data-id="element-187" />
                    </div>}
                </div>)}

              {isTyping && <div className="flex gap-3 justify-start" data-id="element-188">
                  <div className="w-8 h-8 rounded-full flex items-center justify-center p-[2px]" style={{
              background: 'linear-gradient(135deg, rgba(255, 0, 77, 1) 0%, rgba(116, 10, 214, 1) 100%)'
            }} data-id="element-189">
                    <div className="w-full h-full rounded-full flex items-center justify-center bg-white p-1.5" data-id="element-190">
                      <img src="https://cdn.magicpatterns.com/uploads/w4Sqqi6gRHWGegGAQT2PTG/Greenback_logo_(1).png" alt="Greenback" className="w-4 h-4 object-contain" data-id="element-191" />
                    </div>
                  </div>
                  <div className="bg-white border border-gray-200 rounded-2xl px-4 py-3" data-id="element-192">
                    <div className="flex items-center gap-1" data-id="element-193">
                      <div className="w-2 h-2 rounded-full animate-bounce" style={{
                  backgroundColor: '#740AD6'
                }} data-id="element-194"></div>
                      <div className="w-2 h-2 rounded-full animate-bounce" style={{
                  backgroundColor: '#740AD6',
                  animationDelay: '0.2s'
                }} data-id="element-195"></div>
                      <div className="w-2 h-2 rounded-full animate-bounce" style={{
                  backgroundColor: '#740AD6',
                  animationDelay: '0.4s'
                }} data-id="element-196"></div>
                    </div>
                  </div>
                </div>}

              <div ref={messagesEndRef} data-id="element-197" />
            </div>
          </div>

          <div className="p-4 sm:p-6 flex-shrink-0 bg-white border-t border-gray-200" data-id="element-198">
            <form onSubmit={e => {
          e.preventDefault();
          handleSendMessage();
        }} className="max-w-4xl mx-auto relative" data-id="element-199">
              <input ref={inputRef} type="text" value={inputValue} onChange={e => setInputValue(e.target.value)} placeholder="Fai una richiesta..." disabled={isTyping} className="w-full px-6 py-4 pr-14 border border-gray-300 rounded-full focus:outline-none focus:ring-2 focus:border-transparent text-sm font-normal disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-200" />
              <button type="submit" disabled={!inputValue.trim() || isTyping} className="absolute right-2 top-1/2 -translate-y-1/2 p-2.5 bg-green-600 hover:bg-green-700 text-white rounded-full transition-colors disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-201">
                <SendIcon size={18} strokeWidth={1.5} data-id="element-202" />
              </button>
            </form>
          </div>
        </div>}
    </motion.div>;
};
export default GreenbackAIChat;